// App.js

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Toolbar from './components/Toolbar';
import Canvas from './components/Canvas';
import Sidebar from './components/Sidebar';
import { Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Typography, Alert, CircularProgress, Card, CardContent, Menu, MenuItem, Snackbar } from '@mui/material';
import { ViewInAr as ViewInArIcon, ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { styled } from '@mui/system';
import FittingsPanel from './components/FittingsPanel';
import FastenersPanel from './components/FastenersPanel';
import PriceDialog from './components/PriceDialog';
import StartScreen from './components/StartScreen';
import LoginScreen from './components/LoginScreen';
import { AutoConstructorMenu, FloorCollectorEditor, DistributionCollectorEditor, HydraulicSeparatorEditor, CollectorWithSeparatorEditor, CharacteristicsCalculator, CollectorCharacteristicsCalculator, CollectorWithSeparatorCharacteristicsCalculator, FloorCollectorCharacteristicsCalculator } from './components/AutoConstructor';
import {
  exportCurrentDrawingToIGES,
  exportCurrentDrawingToDXF,
  exportCurrentDrawingToSTEP,
  getIgesExportSavePlan,
} from './utils/igesExporter';
import { exportStageToDWG } from './utils/dwgExporter';
import {
  supportsSaveFilePicker,
  supportsDirectoryPicker,
  pickSaveFile,
  pickWritableDirectory,
  persistBlob,
  isAbortError,
} from './utils/fileSystemSave';
import { generateSpecificationPdf } from './utils/specificationPdf';
import { exportWorkshopFastenerFiles } from './utils/fastenerWorkshopFiles';
import { useResponsiveZoom } from './hooks/useResponsiveZoom';
import { useAuth } from './contexts/AuthContext';
import { saveDrawingToCloud, savePtCadDeviceCopy, savePdfDeviceCopy } from './services/drawingStorage';
import { logActivity, startSession, endSession, pingSession, getOrCreateGuestId, finalizeSession } from './services/logActivity';
import SaveDestinationDialog from './components/SaveDestinationDialog';
import PdfWatermarkDialog from './components/PdfWatermarkDialog';
import RequestQuoteDialog from './components/RequestQuoteDialog';
import ModelPreviewDialog from './components/ModelPreviewDialog';
import { loadSheetBackgroundImage } from './utils/sheetBackground';
import { fitGeneratedDrawingToSheet } from './components/AutoConstructor/fitDrawingToSheet';

const ContainerBox = styled(Box)({
  height: 'calc(100% - 4.3125rem)', // 69px (4px красная полоска + 64px toolbar + 1px border)
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  overflow: 'hidden',
});

const CanvasContainer = styled(Box)({
  flex: 1,
  border: '0.0625rem solid #e0e0e0', // 1px
  backgroundColor: '#fafafa',
  position: 'relative',
});

function paramsCalculatedFromCharacteristics(params) {
  if (!params) return false;
  return params._calculatedFrom === 'characteristics' || params.params?._calculatedFrom === 'characteristics';
}

function getEffectiveCalculationMode(autoConstructorParams) {
  if (!autoConstructorParams) return null;
  if (paramsCalculatedFromCharacteristics(autoConstructorParams.params)) {
    return 'parameters';
  }
  return autoConstructorParams.calculationMode || null;
}

function EmailVerificationScreen({ email, onResend, onLogout }) {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleResend() {
    setLoading(true);
    try {
      await onResend();
      setSent(true);
    } catch {
      // ignore
    }
    setLoading(false);
  }

  return (
    <Box sx={{
      minHeight: '100vh', width: '100%', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 50%, #fafafa 100%)',
    }}>
      <Card elevation={3} sx={{ width: '100%', maxWidth: 420, mx: 2, borderRadius: 3, border: '1px solid #f0f0f0' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Box
            component="img"
            src="/proxytherm-logo.png"
            alt="ProxyTherm"
            sx={{ height: 46, width: 'auto', mb: 2, display: 'block', mx: 'auto' }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Подтвердите email</Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Мы отправили письмо на <strong>{email}</strong>.<br />
            Перейдите по ссылке в письме, затем обновите страницу.
          </Typography>
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              borderRadius: 2,
              textAlign: 'left',
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            Не пришло письмо? Обязательно проверьте папку{' '}
            <Box component="span" sx={{ fontWeight: 800, color: '#ed6c02', textTransform: 'uppercase' }}>
              «Спам»
            </Box>
            {' '}или «Нежелательная почта».
          </Alert>
          {sent && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              Письмо отправлено повторно
            </Alert>
          )}
          <Button
            variant="outlined" fullWidth onClick={handleResend} disabled={loading}
            sx={{ mb: 1.5, borderRadius: 2, borderColor: '#d32f2f', color: '#d32f2f',
              '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211,47,47,0.04)' } }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Отправить письмо повторно'}
          </Button>
          <Button
            variant="text" fullWidth onClick={onLogout}
            sx={{ borderRadius: 2, color: '#888', fontSize: '0.85rem' }}
          >
            Выйти
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

function App() {
  // Применяем адаптивный масштаб для разных экранов
  useResponsiveZoom();

  const { currentUser, isAdmin, userInitials, needsVerification, resendVerificationEmail, logout } = useAuth();

  // Ключ автосохранения уникален для каждого пользователя
  const autosaveKey = currentUser ? `cad_autosave_${currentUser.uid}` : 'cad_autosave';

  // Навигация между экранами: 'start' | 'manual' | 'auto'
  const [currentScreen, setCurrentScreen] = useState('start');
  const [guestMode, setGuestMode] = useState(false);
  // Ref для ID текущей гостевой сессии
  const guestSessionRef = useRef(null);
  const [saveDestDialog, setSaveDestDialog] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState(null); // { drawingData, safeFileName }
  const [pdfWatermarkDialog, setPdfWatermarkDialog] = useState(false);
  const [requestQuoteDialog, setRequestQuoteDialog] = useState(false);
  const [autoConstructorType, setAutoConstructorType] = useState(null);
  // Сохранённые параметры автоконструктора для возврата
  const [autoConstructorParams, setAutoConstructorParams] = useState(null);
  // Режим расчета: null (не выбран) | 'characteristics' | 'parameters'
  const [calculationMode, setCalculationMode] = useState(null);
  // Диалог выбора режима расчета
  const [calculationModeDialog, setCalculationModeDialog] = useState(false);
  // Параметры, рассчитанные из характеристик
  const [calculatedParams, setCalculatedParams] = useState(null);
  // Счётчик: инкрементируется каждый раз, когда из калькулятора приходят новые params,
  // чтобы редактор перемонтировался и заново считал initialState
  const [editorMountKey, setEditorMountKey] = useState(0);
  
  const [clearCanvasTrigger, setClearCanvasTrigger] = useState(false);
  const [addCylinderTrigger, setAddCylinderTrigger] = useState(false);
  const [isDrawingEnabled, setIsDrawingEnabled] = useState(false);
  const [isAddAnnotationModeEnabled, setIsAddAnnotationModeEnabled] = useState(false);
  const [isDimensionLineEnabled, setIsDimensionLineEnabled] = useState(false);
  const [isBalloonModeEnabled, setIsBalloonModeEnabled] = useState(false);
  // Сетка и её размер удалены
  const [snapThreshold, setSnapThreshold] = useState(5);
  const [snappingMode, setSnappingMode] = useState('points');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [addFittingTrigger, setAddFittingTrigger] = useState(null);
  const [viewRequest, setViewRequest] = useState({ count: 0, kind: 'top' });
  const [isLandscape, setIsLandscape] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [textZoom, setTextZoom] = useState(1);
  const [positionTextZoom, setPositionTextZoom] = useState(1);
  const [linearPatternTrigger, setLinearPatternTrigger] = useState(false);
  const [linearPatternParams, setLinearPatternParams] = useState(null);

  const [isPriceOpen, setIsPriceOpen] = useState(false);
  const [priceList, setPriceList] = useState(null);
  const getObjectsRef = useRef(null);
  const canvasMetadataRef = useRef(null);
  const [objectsVersion, setObjectsVersion] = useState(0);
  // Триггер для сброса ручного режима в PriceDialog при загрузке нового чертежа
  const [resetPriceManualMode, setResetPriceManualMode] = useState(0);
  // Состояние для визуализации сварок
  const [showWeldMarkers, setShowWeldMarkers] = useState(false);
  
  // Настройки расчета стоимости (сохраняются в файле)
  const [priceCalculationSettings, setPriceCalculationSettings] = useState({
    // Основные настройки
    priceCoefficient: 4,
    
    // Сварка - галочки
    noConduit: true,
    oversizedClassic: true,
    additive: false,
    
    // Сварка - настройки
    manualHeavyCount: null, // null = авто, number = ручное значение
    weldPriceMarkup: 8, // Повышающий коэффициент в процентах
    worksTaxPercent: 60, // Налог на общие работы в процентах
    weldCoefficient: 1, // Коэффициент для итоговой суммы сварки
    
    // Дополнительные расходы
    manualThermometerCaps: null, // null = авто, number = ручное значение
    thermometerCaps: 2, // Заглушки термометр (для обратной совместимости)
    packaging: 100, // Упаковка
    
    // Трубы и слесарка
    pipePriceMarkup: 10, // Плавающие цены труб в процентах
    mirrorPolishingItems: [], // Слесарка за зеркало [{diameter, hours}]
    
    // Присадка по отдельным швам
    perWeldAdditiveMode: false,
    perWeldAdditiveIds: [],
    
    // Ручной ввод суммы сварки
    manualWeldMode: false,
    manualWeldTotal: 0,

    // Ручные переопределения существующих строк комплектующих {rowKey: {name, qty, length, price}}
    componentRowOverrides: {},

    // Дополнительные строки комплектующих [{id, name, qty, length, price}]
    extraComponentRows: []
  });
  
  // Состояние для диалога подтверждения
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    confirmText: 'Подтвердить',
    cancelText: 'Отмена',
  });

  const undoRef = useRef(null);
  const redoRef = useRef(null);
  
  // Refs для сохранения/загрузки чертежа
  const saveDrawingRef = useRef(null);
  const loadDrawingRef = useRef(null);
  const fileInputRef = useRef(null);
  const stageRef = useRef(null);
  const [loadDrawingData, setLoadDrawingData] = useState(null);
  const [pendingRestore, setPendingRestore] = useState(false);
  const [drawingMetadata, setDrawingMetadata] = useState(null); // Метаданные чертежа (totalLength и т.д.)
  const [workshopInfo, setWorkshopInfo] = useState(null); // Данные для цеха (сделка, срок, город, количество)
  const [modelPreviewOpen, setModelPreviewOpen] = useState(false);
  const [viewsMenuAnchor, setViewsMenuAnchor] = useState(null);
  const [guestFeatureNotice, setGuestFeatureNotice] = useState(false);
  const getPreviewDrawing = useCallback(() => {
    const drawingData = saveDrawingRef.current ? saveDrawingRef.current() : null;
    return drawingData?.canvas || null;
  }, []);
  const [weldingTotal, setWeldingTotal] = useState(0); // Итоговая сумма сварки для таблицы цеха

  // ─── Трекинг гостевой сессии ────────────────────────────────────────────────
  useEffect(() => {
    if (!guestMode) return;
    const guestId = getOrCreateGuestId();
    let pingInterval = null;

    startSession({ uid: guestId, email: '', isGuest: true }).then(sessionId => {
      if (sessionId) {
        guestSessionRef.current = { sessionId, startMs: Date.now() };
        // Пингуем каждую минуту — даже если вкладка закроется, lastPing сохранит реальное время
        pingInterval = setInterval(() => {
          if (guestSessionRef.current) {
            pingSession(guestSessionRef.current.sessionId, guestSessionRef.current.startMs);
          }
        }, 60000);
      }
    });

    const handleUnload = () => {
      if (guestSessionRef.current) {
        const { sessionId, startMs } = guestSessionRef.current;
        const durationSec = Math.round((Date.now() - startMs) / 1000);
        try {
          const pending = JSON.parse(localStorage.getItem('ptcad_pending_sessions') || '[]');
          pending.push({ sessionId, durationSec });
          localStorage.setItem('ptcad_pending_sessions', JSON.stringify(pending));
        } catch {}
      }
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      if (pingInterval) clearInterval(pingInterval);
      if (guestSessionRef.current) {
        const { sessionId, startMs } = guestSessionRef.current;
        endSession(sessionId, startMs);
        guestSessionRef.current = null;
      }
    };
  }, [guestMode]);

  // ─── Завершение незакрытых сессий из предыдущего визита ────────────────────
  useEffect(() => {
    try {
      const pending = JSON.parse(localStorage.getItem('ptcad_pending_sessions') || '[]');
      if (pending.length > 0) {
        localStorage.removeItem('ptcad_pending_sessions');
        pending.forEach(({ sessionId, durationSec }) => {
          finalizeSession(sessionId, durationSec).catch(() => {});
        });
      }
    } catch {}
  }, []);

  // ─── Закрытие сессии зарегистрированного пользователя при закрытии вкладки ─
  useEffect(() => {
    if (!currentUser) return;
    const handleUnload = () => {
      if (window.__sessionId && window.__sessionStartMs) {
        const durationSec = Math.round((Date.now() - window.__sessionStartMs) / 1000);
        try {
          const pending = JSON.parse(localStorage.getItem('ptcad_pending_sessions') || '[]');
          pending.push({ sessionId: window.__sessionId, durationSec });
          localStorage.setItem('ptcad_pending_sessions', JSON.stringify(pending));
        } catch {}
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      if (window.__sessionId && window.__sessionStartMs) {
        endSession(window.__sessionId, window.__sessionStartMs);
        window.__sessionId = null;
        window.__sessionStartMs = null;
      }
      window.__workSessionStarted = false;
    };
  }, [currentUser]);

  // Автосохранение: первое через 1 секунду, затем каждые 30 секунд
  useEffect(() => {
    if (currentScreen === 'start') return;
    
    const saveToLocalStorage = () => {
      if (saveDrawingRef.current && currentScreen === 'manual') {
        const drawingData = saveDrawingRef.current();
        
        // Добавляем настройки расчета цены
        drawingData.priceSettings = priceCalculationSettings;
        
        // Добавляем параметры автоконструктора (если есть)
        if (autoConstructorParams) {
          drawingData.autoConstructorParams = autoConstructorParams;
        }
        
        // Добавляем данные для цеха (если есть)
        if (workshopInfo) {
          drawingData.workshopInfo = workshopInfo;
        }
        
        localStorage.setItem(autosaveKey, JSON.stringify(drawingData));
      }
    };
    
    // Первое автосохранение через 1 секунду
    const firstSaveTimeout = setTimeout(saveToLocalStorage, 1000);
    
    // Последующие автосохранения каждые 30 секунд
    const interval = setInterval(saveToLocalStorage, 30000);
    
    return () => {
      clearTimeout(firstSaveTimeout);
      clearInterval(interval);
    };
  }, [currentScreen, priceCalculationSettings, autoConstructorParams, workshopInfo, autosaveKey]);

  // Восстановление чертежа после перехода в ручной конструктор
  useEffect(() => {
    if (!pendingRestore || currentScreen !== 'manual') return;
    
    const savedData = localStorage.getItem(autosaveKey);
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        
        // Восстанавливаем настройки цены
        if (data.priceSettings) {
          if (!data.priceSettings.mirrorPolishingItems) {
            data.priceSettings.mirrorPolishingItems = [];
          }
          if (data.priceSettings.perWeldAdditiveMode === undefined) {
            data.priceSettings.perWeldAdditiveMode = false;
          }
          if (!data.priceSettings.perWeldAdditiveIds) {
            data.priceSettings.perWeldAdditiveIds = [];
          }
          if (data.priceSettings.manualWeldMode === undefined) {
            data.priceSettings.manualWeldMode = false;
          }
          if (data.priceSettings.manualWeldTotal === undefined) {
            data.priceSettings.manualWeldTotal = 0;
          }
          if (!data.priceSettings.componentRowOverrides) {
            data.priceSettings.componentRowOverrides = {};
          }
          if (!data.priceSettings.extraComponentRows) {
            data.priceSettings.extraComponentRows = [];
          }
          setPriceCalculationSettings(data.priceSettings);
        }
        
        // Восстанавливаем параметры автоконструктора
        if (data.autoConstructorParams) {
          setAutoConstructorParams(data.autoConstructorParams);
        }
        
        // Восстанавливаем данные для цеха
        if (data.workshopInfo) {
          setWorkshopInfo(data.workshopInfo);
        }

        // Загружаем настройки
        if (data.settings) {
          if (typeof data.settings.isLandscape === 'boolean') {
            setIsLandscape(data.settings.isLandscape);
          }
          if (typeof data.settings.zoom === 'number') {
            setZoom(data.settings.zoom);
          }
          if (typeof data.settings.textZoom === 'number') {
            setTextZoom(data.settings.textZoom);
          }
          if (typeof data.settings.positionTextZoom === 'number') {
            setPositionTextZoom(data.settings.positionTextZoom);
          }
        }

        setDrawingMetadata(data.canvas?.metadata ?? null);
        
        setTimeout(() => {
          setLoadDrawingData(data);
          setTimeout(() => setLoadDrawingData(null), 100);
          console.log('✅ Автосохранение загружено');
        }, 500);
      } catch (e) {
        console.error('Ошибка при загрузке автосохранения:', e);
      }
    }
    setPendingRestore(false);
  }, [pendingRestore, currentScreen, autosaveKey]);

  // Функция показа диалога подтверждения
  const showConfirmDialog = useCallback((title, message, onConfirm, onCancel = null, confirmText = 'Подтвердить', cancelText = 'Отмена') => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
      onCancel,
      confirmText,
      cancelText,
    });
  }, []);

  // Функция закрытия диалога подтверждения
  const handleCloseConfirmDialog = useCallback(() => {
    if (confirmDialog.onCancel) {
      confirmDialog.onCancel();
    }
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      onConfirm: null,
      onCancel: null,
      confirmText: 'Подтвердить',
      cancelText: 'Отмена',
    });
  }, [confirmDialog]);

  // Функция подтверждения действия
  const handleConfirmAction = useCallback(() => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      onConfirm: null,
      onCancel: null,
      confirmText: 'Подтвердить',
      cancelText: 'Отмена',
    });
  }, [confirmDialog]);

  // Функция перехода в ручной конструктор с проверкой автосохранения
  const handleOpenManualConstructor = useCallback(() => {
    const savedData = localStorage.getItem(autosaveKey);
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        // Проверяем есть ли реальные данные (не пустой чертёж)
        const hasContent = data?.canvas && (
          (data.canvas.fittings?.length > 0) ||
          (data.canvas.cylinders?.length > 0) ||
          (data.canvas.dimensionLines?.length > 0)
        );
        
        if (hasContent) {
          showConfirmDialog(
            'Восстановить чертёж?',
            'Восстановить предыдущий чертёж?',
            () => {
            setPendingRestore(true);
            setCurrentScreen('manual');
            },
            () => {
              localStorage.removeItem(autosaveKey);
              // Сбрасываем workshopInfo при отмене восстановления
              setWorkshopInfo(null);
              setCurrentScreen('manual');
            }
          );
          return;
        }
      } catch (e) {
        console.error('Ошибка при проверке автосохранения:', e);
      }
    }
    setCurrentScreen('manual');
  }, [showConfirmDialog, autosaveKey]);

  // Функции-обёртки для передачи в Sidebar
  const handleUndo = () => {
    if (undoRef.current) {
      undoRef.current();
    }
  };

  const handleRedo = () => {
    if (redoRef.current) {
      redoRef.current();
    }
  };

  // Кнопка сетки удалена

  const handleToggleOrientation = () => {
    setIsLandscape((prev) => !prev);
  };

  const resetAddFittingTrigger = () => {
    setAddFittingTrigger(null);
  };

  const handleClearCanvas = () => {
    setClearCanvasTrigger(!clearCanvasTrigger);
    // Сбрасываем ручной режим цен при очистке (создании нового чертежа)
    setResetPriceManualMode(prev => prev + 1);
    setDrawingMetadata(null);
  };
  
  const resetClearCanvasTrigger = () => {
    setClearCanvasTrigger(false);
  };

  const handleToggleDrawing = () => {
    setIsDrawingEnabled(!isDrawingEnabled);
  };

  const handleToggleAddAnnotationMode = () => {
    setIsAddAnnotationModeEnabled((prev) => !prev);
  };

  const handleToggleDimensionLine = () => {
    setIsDimensionLineEnabled(!isDimensionLineEnabled);
  };

  const handleApplyLinearPattern = (params) => {
    setLinearPatternParams(params || null);
    setLinearPatternTrigger((prev) => !prev);
  };

  const resetApplyLinearPatternTrigger = () => {
    setLinearPatternTrigger(false);
  };

  const handleToggleBalloonMode = () => {
    setIsBalloonModeEnabled(!isBalloonModeEnabled);
  };

  const resetAddCylinderTrigger = () => {
    setAddCylinderTrigger(false);
  };

  // Функция сохранения чертежа
  // Собирает данные чертежа без сохранения
  const prepareDrawingData = useCallback(() => {
    if (!saveDrawingRef.current) return null;
    const drawingData = saveDrawingRef.current();
    drawingData.priceSettings = priceCalculationSettings;
    if (autoConstructorParams) drawingData.autoConstructorParams = autoConstructorParams;
    if (workshopInfo) drawingData.workshopInfo = workshopInfo;
    const artikulAnnotation = drawingData?.canvas?.annotations?.find(a => a.id === 'anno-init-1');
    const artikulText = artikulAnnotation?.text || 'Чертёж';
    const safeFileName = artikulText.replace(/[<>:"/\\|?*]/g, '_').trim() || 'Чертёж';
    return { drawingData, safeFileName };
  }, [priceCalculationSettings, autoConstructorParams, workshopInfo]);

  const logSaveActivity = useCallback((fileName, second) => {
    if (!currentUser?.uid) return;
    const payload = typeof second === 'string'
      ? { fileName, format: 'ptCAD', destination: second }
      : { fileName, ...second };
    logActivity(currentUser.uid, currentUser.email, 'drawing_save', payload);
  }, [currentUser]);

  const getDrawingSafeFileName = useCallback(() => {
    const drawingData = saveDrawingRef.current ? saveDrawingRef.current() : null;
    const artikulAnnotation = drawingData?.canvas?.annotations?.find((a) => a.id === 'anno-init-1');
    const artikulText = artikulAnnotation?.text || 'Чертёж';
    return artikulText.replace(/[<>:"/\\|?*]/g, '_').trim() || 'Чертёж';
  }, []);

  const captureStageThumbnail = useCallback(async () => {
    if (!stageRef.current) return null;
    try {
      const stage = stageRef.current;
      const TW = 400;
      const TH = 260;
      const stagePng = stage.toDataURL({ pixelRatio: 1 });
      return await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const sw = img.naturalWidth;
          const sh = img.naturalHeight;
          const scale = Math.min(TW / sw, TH / sh);
          const dw = Math.round(sw * scale);
          const dh = Math.round(sh * scale);
          const c = document.createElement('canvas');
          c.width = dw;
          c.height = dh;
          const ctx = c.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, dw, dh);
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, dw, dh);
          resolve(c.toDataURL('image/jpeg', 0.88));
        };
        img.onerror = () => resolve(null);
        img.src = stagePng;
      });
    } catch {
      return null;
    }
  }, []);

  // Сохранение на устройство
  const saveToDevice = useCallback(async (drawingData, safeFileName) => {
    const jsonString = JSON.stringify(drawingData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: `${safeFileName}.ptCAD`,
          types: [{ description: 'ProxythermCAD Drawing', accept: { 'application/json': ['.ptCAD'] } }],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Ошибка сохранения:', err);
        return false;
      }
    } else {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeFileName}.ptCAD`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    localStorage.setItem(autosaveKey, jsonString);
    logSaveActivity(safeFileName, 'device');
    if (currentUser?.uid) {
      try {
        const thumbnail = await captureStageThumbnail();
        await savePtCadDeviceCopy(currentUser.uid, drawingData, safeFileName, thumbnail);
      } catch (err) {
        console.warn('Не удалось сохранить копию .ptCAD для админ-панели:', err);
      }
    }
    return true;
  }, [autosaveKey, logSaveActivity, currentUser, captureStageThumbnail]);

  // Сохранение в облако
  const saveToCloud = useCallback(async (drawingData, safeFileName) => {
    if (!currentUser?.uid) return;
    try {
      const thumbnail = await captureStageThumbnail();
      await saveDrawingToCloud(currentUser.uid, drawingData, safeFileName, thumbnail);
      logSaveActivity(safeFileName, 'cloud');
    } catch (err) {
      console.warn('Не удалось сохранить чертёж в облако:', err);
    }
  }, [currentUser, logSaveActivity, captureStageThumbnail]);

  // Кнопка «Сохранить» — открывает диалог выбора
  const handleSaveDrawing = useCallback(() => {
    const prepared = prepareDrawingData();
    if (!prepared) { console.error('Функция сохранения недоступна'); return; }
    setPendingSaveData(prepared);
    setSaveDestDialog(true);
  }, [prepareDrawingData]);

  // Пользователь выбрал «На устройство»
  const handleSaveToDevice = useCallback(async () => {
    setSaveDestDialog(false);
    if (!pendingSaveData) return;
    await saveToDevice(pendingSaveData.drawingData, pendingSaveData.safeFileName);
    setPendingSaveData(null);
  }, [pendingSaveData, saveToDevice]);

  // Пользователь выбрал «В облако»
  const handleSaveToCloud = useCallback(async () => {
    setSaveDestDialog(false);
    if (!pendingSaveData) return;
    await saveToCloud(pendingSaveData.drawingData, pendingSaveData.safeFileName);
    setPendingSaveData(null);
  }, [pendingSaveData, saveToCloud]);

  // Пользователь выбрал «Отправить запрос на расчёт»
  const handleOpenRequestQuote = useCallback(() => {
    // Подготавливаем данные если ещё не подготовлены (имя файла нужно для диалога)
    if (!pendingSaveData) {
      const prepared = prepareDrawingData();
      if (prepared) setPendingSaveData(prepared);
    }
    setSaveDestDialog(false);
    setRequestQuoteDialog(true);
  }, [pendingSaveData, prepareDrawingData]);

  // ─── Общая функция рендера чертежа в PDF-блоб высокого качества ─────────────
  // Рендерит Konva stage в PNG 300 DPI, отправляет на бэкенд → получает PDF.
  // Fallback: если бэкенд недоступен — генерирует PDF локально через jsPDF (PNG без JPEG-артефактов).
  const renderStageToPdfBlob = useCallback(async (stage, { withWatermark = true, fileName = 'Чертёж' } = {}) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

    // Скрываем служебные элементы
    const hideElements = [];
    ['weld-marker', 'snap-point', 'resize-handle', 'hitbox', 'toggle-switch'].forEach(cls => {
      stage.find(`.${cls}`).forEach(node => {
        if (node.visible()) { hideElements.push(node); node.visible(false); }
      });
    });
    stage.find('Transformer').forEach(node => {
      if (node.visible()) { hideElements.push(node); node.visible(false); }
    });

    // Подменяем фон (водяной знак)
    const bgNode = stage.findOne('.sheet-background');
    let prevBgImage = null;
    if (!withWatermark && bgNode) {
      try {
        prevBgImage = bgNode.image();
        const nwImage = await loadSheetBackgroundImage(isLandscape, false);
        bgNode.image(nwImage);
      } catch (err) {
        console.error('Не удалось подставить фон без водяного знака:', err);
      }
    }

    stage.batchDraw();

    // Ждём загрузки всех шрифтов перед рендером (локальный Arimo/Arial)
    try {
      await document.fonts.ready;
    } catch (_) {}

    const stageWidth = stage.width();
    const stageHeight = stage.height();

    // Фиксированный выход: A3 при 150 DPI = 2480×1754 px (landscape) / 1754×2480 px (portrait)
    // Независимо от размера экрана клиента — одинаковое качество на всех устройствах.
    const TARGET_W_PX = isLandscape ? 2480 : 1754;
    const pixelRatio  = Math.max(1.5, TARGET_W_PX / stageWidth);

    console.log(`[PDF Export] stageWidth=${stageWidth}, pixelRatio=${pixelRatio.toFixed(2)}, output=${Math.round(stageWidth * pixelRatio)}x${Math.round(stageHeight * pixelRatio)}px`);

    // Компенсация stroke при toDataURL({ pixelRatio }):
    // Эталон — ваш ПК (pixelRatio ≈ 1.5). На узком экране pixelRatio выше (≈3), и если делить
    // stroke у всех одинаково, трубы/размеры становятся слишком тонкими и «серыми».
    // Фитинги (DXF): strokeScaleEnabled=false — делим на полный pixelRatio.
    // Остальное (цилиндры, размерные линии): делим только до REF_EXPORT_RATIO.
    const REF_EXPORT_RATIO = 1.5;
    if (pixelRatio !== 1) {
      stage.find('Line, Rect, Circle, Arrow, Path, Arc').forEach(node => {
        const sw = node.strokeWidth();
        if (sw <= 0) return;
        node.setAttr('_origSW', sw);
        const exportDivisor = node.strokeScaleEnabled() === false ? pixelRatio : REF_EXPORT_RATIO;
        node.strokeWidth(sw / exportDivisor);
      });
      stage.batchDraw();
    }

    // Снимаем с нужным pixelRatio
    const stageDataUrl = stage.toDataURL({ pixelRatio });

    // Восстанавливаем strokeWidth
    if (pixelRatio !== 1) {
      stage.find('Line, Rect, Circle, Arrow, Path, Arc').forEach(node => {
        const orig = node.getAttr('_origSW');
        if (orig !== undefined) {
          node.strokeWidth(orig);
          node.setAttr('_origSW', undefined);
        }
      });
    }

    // Восстанавливаем фон и скрытые элементы
    if (bgNode && prevBgImage) bgNode.image(prevBgImage);
    hideElements.forEach(node => node.visible(true));
    stage.batchDraw();

    const outW = Math.round(stageWidth * pixelRatio);
    const outH = Math.round(stageHeight * pixelRatio);
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width  = outW;
    tempCanvas.height = outH;
    const ctx = tempCanvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, outW, outH);

    await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { ctx.drawImage(img, 0, 0); resolve(); };
      img.src = stageDataUrl;
    });

    const jpegDataUrl = tempCanvas.toDataURL('image/jpeg', 0.95);
    const pngBase64   = jpegDataUrl.split(',')[1];

    // Отправляем PNG на бэкенд → PDF
    try {
      const response = await fetch(`${BACKEND_URL}/generate-drawing-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          png_base64: pngBase64,
          filename: `${fileName}.pdf`,
          landscape: isLandscape,
        }),
      });
      if (response.ok) {
        const blob = await response.blob();
        console.log('✅ PDF чертежа получен с бэкенда (300 DPI)');
        return blob;
      }
      console.warn('Бэкенд вернул ошибку, fallback на jsPDF');
    } catch (err) {
      console.warn('Бэкенд недоступен, fallback на jsPDF:', err.message);
    }

    // Fallback: jsPDF с JPEG
    const { jsPDF: JsPDF } = await import('jspdf');
    const isLandscapeMode = isLandscape;
    const pdfW = isLandscapeMode ? 420 : 297;
    const pdfH = isLandscapeMode ? 297 : 420;
    const pdf = new JsPDF({ orientation: isLandscapeMode ? 'landscape' : 'portrait', unit: 'mm', format: 'a3', compress: true });
    pdf.addImage(jpegDataUrl, 'JPEG', 0, 0, pdfW, pdfH);
    return pdf.output('blob');
  }, [isLandscape]);

  // Генерация файлов в памяти для отправки по email (без диалога сохранения)
  const handleGenerateFilesForQuote = useCallback(async () => {
    const prepared = prepareDrawingData();
    if (!prepared) throw new Error('Не удалось подготовить данные чертежа');
    const { drawingData, safeFileName } = prepared;

    // .ptCAD blob
    const jsonString = JSON.stringify(drawingData, null, 2);
    const ptCadBlob = new Blob([jsonString], { type: 'application/octet-stream' });

    // PDF blob — генерируем через renderStageToPdfBlob (300 DPI, бэкенд)
    let pdfBlob = null;
    if (stageRef.current) {
      pdfBlob = await renderStageToPdfBlob(stageRef.current, { withWatermark: true, fileName: safeFileName });
    }

    return { ptCadBlob, pdfBlob, fileName: safeFileName };
  }, [prepareDrawingData, renderStageToPdfBlob]);

  const resolvePdfExportNames = useCallback((forWorkshop) => {
    const drawingData = saveDrawingRef.current ? saveDrawingRef.current() : null;
    const artikulAnnotation = drawingData?.canvas?.annotations?.find((a) => a.id === 'anno-init-1');
    const artikulText = artikulAnnotation?.text || 'Чертёж';
    const safeFileName = artikulText.replace(/[<>:"/\\|?*]/g, '_').trim() || 'Чертёж';
    const finalFileName = forWorkshop ? `Для цеха ${safeFileName}` : safeFileName;
    return { finalFileName, downloadName: `${finalFileName}.pdf` };
  }, []);

  // Функция экспорта в PDF
  const handleSaveAsPdf = useCallback(async (options = {}) => {
    const opts = typeof options === 'boolean' ? { forWorkshop: options } : options;
    const {
      forWorkshop = false,
      withWatermark = true,
      directoryHandle: presetDirectoryHandle = null,
      fileHandle: presetFileHandle = null,
      skipSavePicker = false,
    } = opts;

    if (!stageRef.current) {
      console.error('Stage недоступен для экспорта');
      return { saved: false, cancelled: false };
    }

    const stage = stageRef.current;
    const { finalFileName, downloadName } = resolvePdfExportNames(forWorkshop);

    let fileHandle = presetFileHandle;
    let directoryHandle = presetDirectoryHandle;

    if (!directoryHandle && !fileHandle && !skipSavePicker && supportsSaveFilePicker()) {
      try {
        fileHandle = await pickSaveFile({
          suggestedName: downloadName,
          description: 'PDF Document',
          accept: { 'application/pdf': ['.pdf'] },
        });
      } catch (err) {
        if (isAbortError(err)) {
          return { saved: false, cancelled: true };
        }
        console.error('Ошибка выбора файла для PDF:', err);
        return { saved: false, cancelled: false };
      }
    }

    const pdfBlob = await renderStageToPdfBlob(stage, { withWatermark, fileName: finalFileName });

    await persistBlob(pdfBlob, downloadName, { fileHandle, directoryHandle });
    console.log('✅ PDF сохранён');

    logSaveActivity(finalFileName, {
      format: 'pdf',
      withWatermark,
      ...(forWorkshop ? { context: 'workshop' } : {}),
    });
    if (currentUser?.uid) {
      try {
        const thumbnail = await captureStageThumbnail();
        await savePdfDeviceCopy(currentUser.uid, finalFileName, pdfBlob, thumbnail);
      } catch (err) {
        console.warn('Не удалось сохранить копию PDF для админ-панели:', err);
      }
    }
    return { saved: true, cancelled: false };
  }, [
    renderStageToPdfBlob,
    logSaveActivity,
    currentUser,
    captureStageThumbnail,
    resolvePdfExportNames,
  ]);

  const handleOpenPdfSaveDialog = useCallback(() => {
    if (isAdmin) {
      setPdfWatermarkDialog(true);
    } else {
      handleSaveAsPdf({ forWorkshop: false, withWatermark: true });
    }
  }, [isAdmin, handleSaveAsPdf]);

  const handlePdfWithWatermark = useCallback(async () => {
    setPdfWatermarkDialog(false);
    await handleSaveAsPdf({ forWorkshop: false, withWatermark: true });
  }, [handleSaveAsPdf]);

  const handlePdfWithoutWatermark = useCallback(async () => {
    setPdfWatermarkDialog(false);
    await handleSaveAsPdf({ forWorkshop: false, withWatermark: false });
  }, [handleSaveAsPdf]);

  // Сохранение PDF-спецификации (тот же поток, что при «Отправить в цех», но только спецификация и с диалогом сохранения).
  // useMemo, а не useCallback+имя: в react-scripts build eslint не считает {fn} в JSX «использованием» для no-unused-vars.
  const onSaveSpecification = useMemo(
    () => async () => {
      try {
        const drawingDataForSpec = saveDrawingRef.current ? saveDrawingRef.current() : null;
        const artikulAnnotation = drawingDataForSpec?.canvas?.annotations?.find((a) => a.id === 'anno-init-1');
        const artikulText = artikulAnnotation?.text || 'Изделие';
        const safeName = artikulText.replace(/[<>:"/\\|?*]/g, '_').trim() || 'Изделие';
        const pdfName = `Спецификация ${safeName}.pdf`;

        let fileHandle = null;
        if (supportsSaveFilePicker()) {
          try {
            fileHandle = await pickSaveFile({
              suggestedName: pdfName,
              description: 'PDF документ',
              accept: { 'application/pdf': ['.pdf'] },
            });
          } catch (err) {
            if (isAbortError(err)) return;
            throw err;
          }
        }

        const getObjSnapshot = getObjectsRef.current;
        const rawSnapshot = typeof getObjSnapshot === 'function' ? getObjSnapshot() : null;
        const objectsSnapshotForSpec = typeof rawSnapshot === 'function'
          ? rawSnapshot()
          : (rawSnapshot || { fittings: [], cylinders: [] });
        await generateSpecificationPdf(
          objectsSnapshotForSpec,
          artikulText,
          null,
          priceCalculationSettings,
          { fileHandle },
        );
        logSaveActivity(safeName, { format: 'specification_pdf' });
      } catch (e) {
        console.error('Ошибка сохранения спецификации:', e);
        alert('Ошибка сохранения спецификации: ' + e.message);
      }
    },
    [priceCalculationSettings, logSaveActivity]
  );

  // Функция сохранения данных для цеха
  const handleSaveForWorkshop = useCallback((workshopData) => {
    setWorkshopInfo(workshopData);
  }, []);

  const resolveIgesSaveContext = useCallback(async (options = {}) => {
    const {
      directoryHandle: presetDirectoryHandle = null,
      fileHandle: presetFileHandle = null,
      skipPicker = false,
    } = options;

    if (presetDirectoryHandle || presetFileHandle || skipPicker) {
      return {
        saveContext: { directoryHandle: presetDirectoryHandle, fileHandle: presetFileHandle },
        cancelled: false,
      };
    }

    const drawingData = saveDrawingRef.current ? saveDrawingRef.current() : null;
    const canvas = drawingData?.canvas;
    const plan = getIgesExportSavePlan(canvas);
    if (plan.mode === 'error') {
      throw new Error(plan.message);
    }

    if (plan.mode === 'multi' && supportsDirectoryPicker()) {
      try {
        const directoryHandle = await pickWritableDirectory();
        return { saveContext: { directoryHandle, fileHandle: null }, cancelled: false };
      } catch (err) {
        if (isAbortError(err)) return { saveContext: null, cancelled: true };
        throw err;
      }
    }

    if (plan.mode === 'single' && supportsSaveFilePicker()) {
      try {
        const fileHandle = await pickSaveFile({
          suggestedName: plan.suggestedName,
          description: 'IGES файл',
          accept: { 'application/iges': ['.igs', '.iges'] },
        });
        return { saveContext: { directoryHandle: null, fileHandle }, cancelled: false };
      } catch (err) {
        if (isAbortError(err)) return { saveContext: null, cancelled: true };
        throw err;
      }
    }

    return { saveContext: { directoryHandle: null, fileHandle: null }, cancelled: false };
  }, []);

  // Функция экспорта в IGES
  const handleSaveAsIGES = useCallback(async (options = {}) => {
    // Убираем фокус с активного элемента, чтобы избежать предупреждения aria-hidden
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    
    if (!saveDrawingRef.current) {
      console.error('Функция сохранения недоступна');
      alert('Ошибка: не удалось получить данные чертежа');
      return false;
    }
    
    try {
      const { saveContext, cancelled } = await resolveIgesSaveContext(options);
      if (cancelled) return { success: false, cancelled: true, directoryHandle: null };

      const result = await exportCurrentDrawingToIGES(saveDrawingRef.current, saveContext);
      if (result?.success) {
        logSaveActivity(getDrawingSafeFileName(), { format: 'iges' });
      }
      return result;
    } catch (error) {
      console.error('❌ Ошибка при экспорте в IGES:', error);
      alert('Ошибка при экспорте в IGES: ' + error.message);
      return { success: false, directoryHandle: null };
    }
  }, [getDrawingSafeFileName, logSaveActivity, resolveIgesSaveContext]);

  const handleSaveAsSTEP = useCallback(async () => {
    if (!currentUser) return false;
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    if (!saveDrawingRef.current) {
      alert('Ошибка: не удалось получить данные чертежа');
      return false;
    }
    try {
      const drawingData = saveDrawingRef.current();
      const annotations = drawingData?.canvas?.annotations || [];
      const artikul = annotations.find((a) => a.id === 'anno-init-1')?.text?.trim() || 'Изделие';
      const suggestedName = `${artikul.replace(/[<>:"/\\|?*]/g, '_')}.stp`;
      let fileHandle = null;
      if (supportsSaveFilePicker()) {
        try {
          fileHandle = await pickSaveFile({
            suggestedName,
            description: 'STEP файл',
            accept: { 'application/step': ['.stp', '.step'] },
          });
        } catch (err) {
          if (isAbortError(err)) return { success: false, cancelled: true };
          throw err;
        }
      }
      const result = await exportCurrentDrawingToSTEP(saveDrawingRef.current, {
        directoryHandle: null,
        fileHandle,
      });
      if (result?.success) {
        logSaveActivity(getDrawingSafeFileName(), { format: 'step' });
      }
      return result;
    } catch (error) {
      console.error('Ошибка при экспорте в STEP:', error);
      alert('Ошибка при экспорте в STEP: ' + error.message);
      return { success: false };
    }
  }, [currentUser, getDrawingSafeFileName, logSaveActivity]);

  const handleSaveAsDwg = useCallback(async () => {
    if (!currentUser) return false;
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    if (!stageRef.current) {
      alert('Холст недоступен для экспорта');
      return false;
    }
    const { finalFileName } = resolvePdfExportNames(false);
    const downloadName = `${finalFileName}.dwg`;
    let fileHandle = null;
    if (supportsSaveFilePicker()) {
      try {
        fileHandle = await pickSaveFile({
          suggestedName: downloadName,
          description: 'DWG чертёж',
          accept: { 'application/acad': ['.dwg'] },
        });
      } catch (err) {
        if (isAbortError(err)) return { success: false, cancelled: true };
        console.error('Ошибка выбора файла DWG:', err);
        return { success: false };
      }
    }
    try {
      await exportStageToDWG(stageRef.current, { fileName: finalFileName, fileHandle });
      logSaveActivity(finalFileName, { format: 'dwg' });
      return { success: true };
    } catch (error) {
      console.error('Ошибка при экспорте в DWG:', error);
      alert('Ошибка при экспорте в DWG: ' + error.message);
      return { success: false };
    }
  }, [currentUser, logSaveActivity, resolvePdfExportNames]);

  // Функция экспорта в DXF (для компактного коллектора)
  const handleSaveAsDXF = useCallback(async (options = {}) => {
    // Убираем фокус с активного элемента, чтобы избежать предупреждения aria-hidden
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    
    if (!saveDrawingRef.current) {
      console.error('Функция сохранения недоступна');
      alert('Ошибка: не удалось получить данные чертежа');
      return false;
    }
    
    try {
      let saveContext = {
        directoryHandle: options.directoryHandle || null,
        fileHandle: options.fileHandle || null,
      };
      if (!options.skipPicker && !saveContext.directoryHandle && !saveContext.fileHandle) {
        if (supportsDirectoryPicker()) {
          try {
            saveContext.directoryHandle = await pickWritableDirectory();
          } catch (err) {
            if (isAbortError(err)) return { success: false, cancelled: true };
            throw err;
          }
        }
      }

      const result = await exportCurrentDrawingToDXF(saveDrawingRef.current, saveContext);
      if (result === true) {
        logSaveActivity(getDrawingSafeFileName(), { format: 'dxf' });
      }
      return result;
    } catch (error) {
      console.error('❌ Ошибка при экспорте в DXF:', error);
      alert('Ошибка при экспорте в DXF: ' + error.message);
      return false;
    }
  }, [getDrawingSafeFileName, logSaveActivity]);

  const handleSaveCuttingModels = useCallback(async () => {
    if (document.activeElement?.blur) {
      document.activeElement.blur();
    }
    let sharedDirectoryHandle = null;
    if (supportsDirectoryPicker()) {
      try {
        sharedDirectoryHandle = await pickWritableDirectory();
      } catch (err) {
        if (isAbortError(err)) return;
        console.warn('Не удалось выбрать папку, файлы будут в загрузках:', err.message);
      }
    }

    const igesResult = await handleSaveAsIGES({
      directoryHandle: sharedDirectoryHandle,
      fileHandle: null,
      skipPicker: true,
    });
    if (igesResult?.cancelled) return;
    if (igesResult === false || (igesResult && !igesResult.success)) {
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 500));
    await handleSaveAsDXF({
      directoryHandle: sharedDirectoryHandle,
      skipPicker: true,
    });
  }, [handleSaveAsIGES, handleSaveAsDXF]);

  const handleSendToWorkshop = useCallback(async () => {
    if (!workshopInfo) {
      alert('Ошибка: данные цеха не заполнены');
      return;
    }

    try {
      let workshopDirectoryHandle = null;
      if (supportsDirectoryPicker()) {
        try {
          workshopDirectoryHandle = await pickWritableDirectory();
        } catch (err) {
          if (isAbortError(err)) return;
          console.warn('Не удалось выбрать папку, файлы будут в загрузках:', err.message);
        }
      }

      console.log('Шаг 1: Сохранение PDF...');
      const pdfResult = await handleSaveAsPdf({
        forWorkshop: true,
        withWatermark: false,
        directoryHandle: workshopDirectoryHandle,
        skipSavePicker: true,
      });
      if (pdfResult?.cancelled) return;

      console.log('Шаг 2: Сохранение моделей для резки...');
      const igesResult = await handleSaveAsIGES({
        directoryHandle: workshopDirectoryHandle,
        skipPicker: true,
      });
      if (igesResult?.cancelled) return;
      if (igesResult && typeof igesResult === 'object' && !igesResult.success) {
        console.warn('IGES экспорт не удался');
      }

      await new Promise((resolve) => setTimeout(resolve, 500));
      await handleSaveAsDXF({
        directoryHandle: workshopDirectoryHandle,
        skipPicker: true,
      });

      console.log('Шаг 3: Крепежи с новым названием...');
      try {
        const getFastenerSnapshot = getObjectsRef.current;
        const rawFastenerSnapshot = typeof getFastenerSnapshot === 'function' ? getFastenerSnapshot() : null;
        const fastenerObjects = typeof rawFastenerSnapshot === 'function'
          ? rawFastenerSnapshot()
          : (rawFastenerSnapshot || { fittings: [] });
        const drawingForTitle = saveDrawingRef.current ? saveDrawingRef.current() : null;
        const productTitle = drawingForTitle?.canvas?.annotations?.find((a) => a.id === 'anno-init-1')?.text || '';
        const fastenerFailed = await exportWorkshopFastenerFiles(
          fastenerObjects.fittings,
          workshopDirectoryHandle,
          workshopInfo,
          productTitle,
        );
        if (fastenerFailed.length > 0) {
          alert(`Не удалось подготовить файлы крепежа для цеха:\n${fastenerFailed.join('\n')}`);
        }
      } catch (fastenerError) {
        console.error('Ошибка файлов крепежа для цеха:', fastenerError);
        alert('Не удалось подготовить файлы крепежа для цеха: ' + fastenerError.message);
      }

      console.log('Шаг 4: Генерация спецификации...');
      try {
        const drawingDataForSpec = saveDrawingRef.current ? saveDrawingRef.current() : null;
        const artikulAnnotation = drawingDataForSpec?.canvas?.annotations?.find((a) => a.id === 'anno-init-1');
        const artikulText = artikulAnnotation?.text || 'Изделие';
        const getObjSnapshot = getObjectsRef.current;
        const rawSnapshot = typeof getObjSnapshot === 'function' ? getObjSnapshot() : null;
        const objectsSnapshotForSpec = typeof rawSnapshot === 'function'
          ? rawSnapshot()
          : (rawSnapshot || { fittings: [], cylinders: [] });
        await generateSpecificationPdf(
          objectsSnapshotForSpec,
          artikulText,
          workshopDirectoryHandle,
          priceCalculationSettings,
        );
      } catch (specError) {
        console.error('Ошибка генерации спецификации:', specError);
      }

      console.log('✅ Отправка в цех завершена');
    } catch (error) {
      console.error('Ошибка при отправке в цех:', error);
      alert('Ошибка при отправке в цех: ' + error.message);
    }
  }, [workshopInfo, handleSaveAsPdf, handleSaveAsIGES, handleSaveAsDXF, priceCalculationSettings]);

  // Функция загрузки чертежа
  const handleLoadDrawing = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Общая логика применения данных чертежа (используется и при открытии файла, и из истории)
  const applyDrawingData = useCallback((data) => {
        // Восстанавливаем настройки цены
        if (data.priceSettings) {
          if (!data.priceSettings.mirrorPolishingItems) {
            data.priceSettings.mirrorPolishingItems = [];
          }
          if (data.priceSettings.perWeldAdditiveMode === undefined) {
            data.priceSettings.perWeldAdditiveMode = false;
          }
          if (!data.priceSettings.perWeldAdditiveIds) {
            data.priceSettings.perWeldAdditiveIds = [];
          }
          if (data.priceSettings.manualWeldMode === undefined) {
            data.priceSettings.manualWeldMode = false;
          }
          if (data.priceSettings.manualWeldTotal === undefined) {
            data.priceSettings.manualWeldTotal = 0;
          }
          if (!data.priceSettings.componentRowOverrides) {
            data.priceSettings.componentRowOverrides = {};
          }
          if (!data.priceSettings.extraComponentRows) {
            data.priceSettings.extraComponentRows = [];
          }
          setPriceCalculationSettings(data.priceSettings);
        }
        if (data.autoConstructorParams) {
          setAutoConstructorParams(data.autoConstructorParams);
        } else {
          setAutoConstructorParams(null);
        }
        if (data.workshopInfo) {
          setWorkshopInfo(data.workshopInfo);
          setTimeout(() => setIsPriceOpen(true), 600);
        } else {
          setWorkshopInfo(null);
        }
        if (data.settings) {
          if (typeof data.settings.isLandscape === 'boolean') setIsLandscape(data.settings.isLandscape);
          if (typeof data.settings.zoom === 'number') setZoom(data.settings.zoom);
          if (typeof data.settings.textZoom === 'number') setTextZoom(data.settings.textZoom);
          if (typeof data.settings.positionTextZoom === 'number') setPositionTextZoom(data.settings.positionTextZoom);
        }
        setDrawingMetadata(data.canvas?.metadata ?? null);
        setLoadDrawingData(data);
        setTimeout(() => setLoadDrawingData(null), 100);
        setCurrentScreen('manual');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Открыть чертёж из истории
  const handleOpenDrawingFromHistory = useCallback((data, fileName) => {
    applyDrawingData(data);
    if (currentUser?.uid) {
      logActivity(currentUser.uid, currentUser.email, 'drawing_open', { fileName: fileName || 'неизвестно', source: 'history' });
    }
  }, [applyDrawingData, currentUser]);

  // Обработка выбора файла
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        applyDrawingData(data);
        if (currentUser?.uid) {
          logActivity(currentUser.uid, currentUser.email, 'drawing_open', { fileName: file.name, source: 'local_file' });
        }
      } catch (error) {
        console.error('Ошибка при чтении файла:', error);
        alert('Ошибка: неверный формат файла');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  // Функция создания нового чертежа
  const handleNewDrawing = () => {
    showConfirmDialog(
      'Создать новый чертёж?',
      'Несохранённые изменения будут потеряны.',
      () => {
      // Очищаем автосохранение
      localStorage.removeItem(autosaveKey);
      // Сбрасываем масштаб к значениям по умолчанию
      setZoom(1);
      setTextZoom(1);
      setPositionTextZoom(1);
      // Сбрасываем ручной режим цен
      setResetPriceManualMode(prev => prev + 1);
      setPriceCalculationSettings({
        noConduit: true, // По умолчанию включено
        oversizedClassic: true, // По умолчанию включено
        manualHeavyCount: null,
        manualThermometerCaps: null,
        manualAdditive: null,
        priceCoefficient: 4
      });
      // Очищаем данные цеха
      setWorkshopInfo(null);
      setDrawingMetadata(null);
      // Очищаем холст
      setClearCanvasTrigger(!clearCanvasTrigger);
    }
    );
  };

  // Обработка генерации чертежа из автоконструктора
  const handleAutoGenerate = useCallback((sourceDrawing, savedParams) => {
    const drawingData = fitGeneratedDrawingToSheet(sourceDrawing);

    // Сохраняем параметры автоконструктора для возможности вернуться
    const savedAutoConstructorParams = savedParams ? {
      type: autoConstructorType,
      calculationMode: 'parameters',
      params: savedParams,
    } : null;

    if (savedAutoConstructorParams) {
      setAutoConstructorParams(savedAutoConstructorParams);
    }
    
    // Применяем настройки из сгенерированного чертежа
    if (drawingData.settings) {
      if (typeof drawingData.settings.isLandscape === 'boolean') {
        setIsLandscape(drawingData.settings.isLandscape);
      }
      if (typeof drawingData.settings.zoom === 'number') {
        setZoom(drawingData.settings.zoom);
      }
      if (typeof drawingData.settings.textZoom === 'number') {
        setTextZoom(drawingData.settings.textZoom);
      }
      if (typeof drawingData.settings.positionTextZoom === 'number') {
        setPositionTextZoom(drawingData.settings.positionTextZoom);
      }
    }
    
    // Сохраняем метаданные чертежа (totalLength и т.д.)
    if (drawingData.metadata) {
      setDrawingMetadata(drawingData.metadata);
      // Метаданные будут переданы в Canvas через loadData.canvas.metadata
    }
    
    // Очищаем mirrorPolishingItems и сбрасываем ручные значения при генерации нового чертежа
    // Они будут заполнены заново при переключении на "Зеркало" (если нужно)
    setPriceCalculationSettings(prev => ({
      ...prev,
      mirrorPolishingItems: [],
      manualThermometerCaps: null, // Сбрасываем ручное значение заглушек термометра
      manualHeavyCount: null, // Сбрасываем ручное значение "Более 25кг/2м"
      manualAdditive: null, // Сбрасываем ручное значение присадки
      noConduit: true, // При генерации включаем "Нет кондуктора"
      oversizedClassic: true, // При генерации включаем "Габариты больше CLASSIC"
      componentRowOverrides: {}, // Сбрасываем ручные изменения комплектующих
      extraComponentRows: [] // Сбрасываем дополнительные строки комплектующих
    }));
    
    // Очищаем данные цеха при генерации нового чертежа
    setWorkshopInfo(null);
    
    // Формируем данные для загрузки
    const loadData = {
      canvas: {
        fittings: drawingData.fittings || [],
        cylinders: drawingData.cylinders || [],
        welds: drawingData.welds || [],
        annotations: drawingData.annotations || [],
        balloons: drawingData.balloons || [],
        dimensionLines: drawingData.dimensionLines || [],
        lines: drawingData.lines || [],
        objectConstraints: drawingData.objectConstraints || [],
        metadata: drawingData.metadata || {}, // Добавляем метаданные (включая покрытие)
      },
      settings: drawingData.settings || {},
      isNewGeneration: true, // Флаг, что это новая генерация из автоконструктора
    };
    
    // Загружаем чертёж
    setLoadDrawingData(loadData);
    setTimeout(() => setLoadDrawingData(null), 100);
    
    // Переключаемся на ручной редактор
    setAutoConstructorType(null);
    setCurrentScreen('manual');
    
    // Сохраняем сгенерированный чертёж в localStorage с задержкой,
    // чтобы Canvas успел загрузить данные и добавить начальные аннотации
    setTimeout(() => {
      if (saveDrawingRef.current) {
        const drawingData = saveDrawingRef.current();
        
        // Добавляем настройки расчета цены
        drawingData.priceSettings = priceCalculationSettings;
        
        // Добавляем параметры автоконструктора (если есть)
        if (savedAutoConstructorParams) {
          drawingData.autoConstructorParams = savedAutoConstructorParams;
        }
        
        localStorage.setItem(autosaveKey, JSON.stringify(drawingData));
      }
    }, 1000); // Задержка 1 секунда
  }, [autoConstructorType, priceCalculationSettings, autosaveKey]);

  // Функция возврата на главный экран
  const handleGoHome = useCallback(() => {
    showConfirmDialog(
      'Вернуться на главный экран?',
      'Несохранённые изменения будут потеряны.',
      () => {
      setCurrentScreen('start');
      setAutoConstructorType(null);
      setAutoConstructorParams(null);
    }
    );
  }, [showConfirmDialog]);

  // Функция возврата к параметрам автоконструктора
  const handleBackToAutoConstructor = useCallback(() => {
    if (autoConstructorParams) {
      // Сохраняем текущий чертёж перед переходом к параметрам
      if (saveDrawingRef.current) {
        const drawingData = saveDrawingRef.current();
        
        // Добавляем настройки расчета цены
        drawingData.priceSettings = priceCalculationSettings;
        
        // Добавляем параметры автоконструктора
        drawingData.autoConstructorParams = autoConstructorParams;
        
        localStorage.setItem(autosaveKey, JSON.stringify(drawingData));
      }
      
      setAutoConstructorType(autoConstructorParams.type);
      // Восстанавливаем режим расчёта: если параметры уже рассчитаны из характеристик —
      // открываем редактор параметров, а не калькулятор характеристик
      const mode = getEffectiveCalculationMode(autoConstructorParams);
      if (mode) {
        setCalculationMode(mode);
      } else {
        // Для старых файлов без сохранённого calculationMode — открываем диалог выбора
        setCalculationModeDialog(true);
      }
      setCurrentScreen('auto');
    }
  }, [autoConstructorParams, priceCalculationSettings, autosaveKey]);

  // Функция возврата на главный экран из автоконструктора
  const handleBackFromAutoConstructor = useCallback(() => {
    showConfirmDialog(
      'Вернуться на главный экран?',
      'Несохранённые изменения будут потеряны.',
      () => {
        setAutoConstructorType(null);
        setCalculationMode(null);
        setCalculationModeDialog(false);
        setCalculatedParams(null);
      }
    );
  }, [showConfirmDialog]);

  // Функция возврата к чертежу из автоконструктора
  const handleBackToDrawing = useCallback(() => {
    showConfirmDialog(
      'Вернуться к чертежу?',
      'Если параметры были изменены, следует сгенерировать новый чертёж.',
      () => {
        // Вернуться к исходному чертежу - загружаем сохраненный чертеж
        const savedData = localStorage.getItem(autosaveKey);
        if (savedData) {
          try {
            const data = JSON.parse(savedData);
            
            // Восстанавливаем настройки цены
            if (data.priceSettings) {
              if (!data.priceSettings.mirrorPolishingItems) {
                data.priceSettings.mirrorPolishingItems = [];
              }
              if (data.priceSettings.perWeldAdditiveMode === undefined) {
                data.priceSettings.perWeldAdditiveMode = false;
              }
              if (!data.priceSettings.perWeldAdditiveIds) {
                data.priceSettings.perWeldAdditiveIds = [];
              }
              if (data.priceSettings.manualWeldMode === undefined) {
                data.priceSettings.manualWeldMode = false;
              }
              if (data.priceSettings.manualWeldTotal === undefined) {
                data.priceSettings.manualWeldTotal = 0;
              }
              setPriceCalculationSettings(data.priceSettings);
            }
            
            // Восстанавливаем параметры автоконструктора
            if (data.autoConstructorParams) {
              setAutoConstructorParams(data.autoConstructorParams);
            }
            
            // Восстанавливаем данные для цеха
            if (data.workshopInfo) {
              setWorkshopInfo(data.workshopInfo);
            }

            // Загружаем настройки
            if (data.settings) {
              if (typeof data.settings.isLandscape === 'boolean') {
                setIsLandscape(data.settings.isLandscape);
              }
              if (typeof data.settings.zoom === 'number') {
                setZoom(data.settings.zoom);
              }
              if (typeof data.settings.textZoom === 'number') {
                setTextZoom(data.settings.textZoom);
              }
              if (typeof data.settings.positionTextZoom === 'number') {
                setPositionTextZoom(data.settings.positionTextZoom);
              }
            }
            
            // Загружаем чертёж
            setLoadDrawingData(data);
            setTimeout(() => setLoadDrawingData(null), 100);
            
            // Переключаемся на ручной редактор
            setCurrentScreen('manual');
          } catch (e) {
            console.error('Ошибка при загрузке чертежа:', e);
            setCurrentScreen('manual');
          }
        } else {
          setCurrentScreen('manual');
        }
      },
      () => {
        // Вернуться к параметрам - просто закрываем диалог, остаемся в редакторе
        // Ничего не делаем, диалог закроется автоматически
      },
      'Вернуться к исходному',
      'Вернуться к параметрам'
    );
  }, [showConfirmDialog, autosaveKey]);

  // Получение редактора по типу изделия
  const getEditorByType = (type) => {
    switch (type) {
      case 'floor_collector':
        return FloorCollectorEditor;
      case 'distribution_collector':
        return DistributionCollectorEditor;
      case 'hydraulic_separator':
        return HydraulicSeparatorEditor;
      case 'collector_with_separator':
        return CollectorWithSeparatorEditor;
      default:
        return null;
    }
  };

  // Рендер экрана авторизации
  if (!currentUser && !guestMode) {
    return <LoginScreen onContinueAsGuest={() => setGuestMode(true)} />;
  }

  // Рендер экрана подтверждения email (только для зарегистрированных через сайт)
  if (needsVerification) {
    return <EmailVerificationScreen email={currentUser.email} onResend={resendVerificationEmail} onLogout={logout} />;
  }

  // Рендер стартового экрана
  if (currentScreen === 'start') {
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <StartScreen 
          onSelectMode={(mode) => {
            if (mode === 'manual') {
              handleOpenManualConstructor();
            } else {
              setCurrentScreen(mode);
            }
          }}
          onOpenDrawingFromHistory={handleOpenDrawingFromHistory}
        />
        
        {/* Диалог подтверждения */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleCloseConfirmDialog}
        >
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmDialog.message}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDialog} color="inherit">
              {confirmDialog.cancelText}
            </Button>
            <Button onClick={handleConfirmAction} color="primary" variant="contained" autoFocus>
              {confirmDialog.confirmText}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  // Рендер автоконструктора
  if (currentScreen === 'auto') {
    let content;
    
    // Если тип не выбран - показываем меню выбора
    if (!autoConstructorType) {
      content = (
        <AutoConstructorMenu
          onSelectType={(type) => {
            setAutoConstructorType(type);
            // Открываем диалог выбора режима расчёта для поддерживаемых типов
            if (type === 'hydraulic_separator' || type === 'distribution_collector' || type === 'collector_with_separator' || type === 'floor_collector') {
              setCalculationModeDialog(true);
            } else {
              // Для других устройств — ручной ввод
              setCalculationMode('parameters');
            }
          }}
          onBack={() => setCurrentScreen('start')}
        />
      );
    } else if ((autoConstructorType === 'hydraulic_separator' || autoConstructorType === 'distribution_collector' || autoConstructorType === 'collector_with_separator' || autoConstructorType === 'floor_collector') && !calculationMode) {
      // Показываем диалог выбора режима расчёта
      content = null; // Диалог будет отрисован ниже
    } else if (autoConstructorType === 'floor_collector' && calculationMode === 'characteristics' && !calculatedParams) {
      content = (
        <FloorCollectorCharacteristicsCalculator
          key={autoConstructorParams?.params?.params?._characteristics ? 'with-params' : 'no-params'}
          onBack={handleBackFromAutoConstructor}
          onBackToDrawing={autoConstructorParams ? handleBackToDrawing : null}
          initialParams={(() => {
            const ap = autoConstructorParams?.type === autoConstructorType ? autoConstructorParams.params : null;
            if (!ap) return null;
            // После возврата из редактора ap = { params, circuits, bottomCircuits, centerFittings }
            if (ap._characteristics) return ap;
            if (ap.params?._characteristics) return ap.params;
            return ap;
          })()}
          onCalculate={(savedParams) => {
            setCalculatedParams(savedParams);
            setEditorMountKey(k => k + 1);
            setCalculationMode('parameters');
            setAutoConstructorParams({ type: autoConstructorType, calculationMode: 'parameters', params: savedParams });
          }}
        />
      );
    } else if (autoConstructorType === 'hydraulic_separator' && calculationMode === 'characteristics' && !calculatedParams) {
      // Калькулятор характеристик для гидроразделителя
      content = (
        <CharacteristicsCalculator
          key={autoConstructorParams?.params?._characteristics ? 'with-params' : 'no-params'}
          onBack={handleBackFromAutoConstructor}
          onBackToDrawing={autoConstructorParams ? handleBackToDrawing : null}
          initialParams={autoConstructorParams?.type === autoConstructorType ? autoConstructorParams.params : null}
          onCalculate={(params) => {
            setCalculatedParams(params);
            setEditorMountKey(k => k + 1);
            setCalculationMode('parameters');
            setAutoConstructorParams({
              type: autoConstructorType,
              calculationMode: 'parameters',
              params: params,
            });
          }}
        />
      );
    } else if (autoConstructorType === 'collector_with_separator' && calculationMode === 'characteristics' && !calculatedParams) {
      content = (
        <CollectorWithSeparatorCharacteristicsCalculator
          key={autoConstructorParams?.params?._characteristics || autoConstructorParams?.params?.params?._characteristics ? 'with-params' : 'no-params'}
          onBack={handleBackFromAutoConstructor}
          onBackToDrawing={autoConstructorParams ? handleBackToDrawing : null}
          initialParams={(() => {
            const ap = autoConstructorParams?.type === autoConstructorType ? autoConstructorParams.params : null;
            if (!ap) return null;
            // До генерации params плоский (снимок калькулятора). После генерации — обёртка редактора { params, supplyCircuits, ... }.
            if (ap._characteristics) return ap;
            if (ap.params?._characteristics) return ap.params;
            return ap;
          })()}
          onCalculate={(params) => {
            setCalculatedParams(params);
            setEditorMountKey(k => k + 1);
            setCalculationMode('parameters');
            setAutoConstructorParams({
              type: autoConstructorType,
              calculationMode: 'parameters',
              params: params,
            });
          }}
        />
      );
    } else if (autoConstructorType === 'distribution_collector' && calculationMode === 'characteristics' && !calculatedParams) {
      // Калькулятор характеристик для распределительного коллектора
      content = (
        <CollectorCharacteristicsCalculator
          key={autoConstructorParams?.params?._characteristics || autoConstructorParams?.params?.params?._characteristics ? 'with-params' : 'no-params'}
          onBack={handleBackFromAutoConstructor}
          onBackToDrawing={autoConstructorParams ? handleBackToDrawing : null}
          initialParams={(() => {
            const ap = autoConstructorParams?.type === autoConstructorType ? autoConstructorParams.params : null;
            if (!ap) return null;
            // После возврата из редактора autoConstructorParams.params имеет формат { params: {...}, supplyCircuits: [] }
            // Калькулятор ожидает плоский объект с _characteristics
            if (ap._characteristics) return ap;          // уже плоский (из калькулятора)
            if (ap.params?._characteristics) return ap.params; // из редактора — берём вложенный params
            return ap;
          })()}
          onCalculate={(params) => {
            setCalculatedParams(params);
            setEditorMountKey(k => k + 1);
            setCalculationMode('parameters');
            setAutoConstructorParams({
              type: autoConstructorType,
              calculationMode: 'parameters',
              params: params,
            });
          }}
        />
      );
    } else {
    // Получаем редактор для выбранного типа
    const Editor = getEditorByType(autoConstructorType);
    
    if (!Editor) {
      // Редактор ещё не реализован
        content = (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <h2>Редактор для данного типа пока не реализован</h2>
          <button onClick={() => setAutoConstructorType(null)}>Назад</button>
        </Box>
      );
      } else {
        // Определяем начальные параметры:
        // 1. Если есть сохраненные параметры из autoConstructorParams - используем их
        // 2. Если есть рассчитанные параметры из калькулятора - используем их
        // 3. Иначе - null (будут использованы дефолтные)
        let initialParams = null;
        if (autoConstructorParams?.type === autoConstructorType) {
          const ap = autoConstructorParams.params;
          // DistributionCollectorEditor ожидает { params: {...}, supplyCircuits: [...], ... }
          // Если ap пришёл из калькулятора — он плоский (содержит _calculatedFrom).
          // Если ap пришёл из редактора — он уже обёрнутый (содержит вложенный .params).
          if (
            autoConstructorType === 'distribution_collector' ||
            autoConstructorType === 'collector_with_separator'
          ) {
            if (ap && !ap.params) {
              // Плоский объект из калькулятора — оборачиваем
              initialParams = {
                params: ap,
                supplyCircuits: ap.supplyCircuits ?? [],
                returnCircuits: ap.returnCircuits ?? [],
                supplyCenterFittings: ap.supplyCenterFittings ?? [],
                returnCenterFittings: ap.returnCenterFittings ?? [],
                supplyAdditionalOutlets: ap.supplyAdditionalOutlets ?? [],
                returnAdditionalOutlets: ap.returnAdditionalOutlets ?? [],
              };
            } else {
              // Уже обёрнутый объект из редактора
              initialParams = ap;
            }
          } else {
            initialParams = ap;
          }
        }
        
        content = (
          <Editor
            key={editorMountKey}
            onGenerate={handleAutoGenerate}
            onBack={handleBackFromAutoConstructor}
            onBackToDrawing={autoConstructorParams ? handleBackToDrawing : null}
            initialParams={initialParams}
            calculationMode={calculationMode}
            onBackToCharacteristics={
              paramsCalculatedFromCharacteristics(autoConstructorParams?.params)
                ? () => {
                    setCalculatedParams(null);
                    setCalculationMode('characteristics');
                  }
                : undefined
            }
          />
        );
      }
    }
    
    return (
      <div style={{ width: '100%', minHeight: '100%', overflow: 'visible' }}>
        {content}
        
        {/* Диалог выбора режима расчета */}
        <Dialog
          open={calculationModeDialog}
          onClose={() => {
            setCalculationModeDialog(false);
            setAutoConstructorType(null);
            setCalculationMode(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Выбор режима расчета</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Выберите режим расчёта:
            </DialogContentText>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  setCalculationMode('characteristics');
                  setCalculationModeDialog(false);
                  // Сбрасываем старые параметры при новом выборе режима
                  setAutoConstructorParams(null);
                  setCalculatedParams(null);
                  const setCharacteristicsEngineersViewIfAllowed = (storageKey) => {
                    if (!isAdmin) return;
                    try {
                      localStorage.setItem(storageKey, 'engineers');
                    } catch (_) {}
                  };
                  // Очищаем localStorage, чтобы калькулятор начинал с дефолтных значений
                  if (autoConstructorType === 'hydraulic_separator') {
                    localStorage.removeItem('hydraulicSeparatorCharacteristics');
                    localStorage.removeItem('hydraulicSeparatorCharacteristicsViewMode');
                    setCharacteristicsEngineersViewIfAllowed('hydraulicSeparatorCharacteristicsViewMode');
                  } else if (autoConstructorType === 'distribution_collector') {
                    localStorage.removeItem('collectorCharacteristics');
                    localStorage.removeItem('collectorCharacteristicsViewMode');
                    setCharacteristicsEngineersViewIfAllowed('collectorCharacteristicsViewMode');
                  } else if (autoConstructorType === 'collector_with_separator') {
                    localStorage.removeItem('cwsCharacteristics');
                    localStorage.removeItem('cwsCharacteristicsViewMode');
                    setCharacteristicsEngineersViewIfAllowed('cwsCharacteristicsViewMode');
                  } else if (autoConstructorType === 'floor_collector') {
                    localStorage.removeItem('floorCollectorCharacteristics');
                  }
                }}
                sx={{
                  py: 2,
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    1. Расчет по характеристикам
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Введите характеристики (расход, температуру и т.д.), и параметры будут рассчитаны автоматически
                  </Typography>
                </Box>
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  setCalculationMode('parameters');
                  setCalculationModeDialog(false);
                }}
                sx={{
                  py: 2,
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    2. Расчет по параметрам
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Прямой переход к настройке параметров
                  </Typography>
                </Box>
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setCalculationModeDialog(false);
                setAutoConstructorType(null);
                setCalculationMode(null);
              }}
            >
              Отмена
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Диалог подтверждения */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleCloseConfirmDialog}
        >
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmDialog.message}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDialog} color="inherit">
              {confirmDialog.cancelText}
            </Button>
            <Button onClick={handleConfirmAction} color="primary" variant="contained" autoFocus>
              {confirmDialog.confirmText}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  // Рендер ручного конструктора (текущая функциональность)
  return (
    <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
      {/* Скрытый input для загрузки файла */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.ptCAD"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      <Toolbar
        zoom={zoom}
        setZoom={setZoom}
        textZoom={textZoom}
        setTextZoom={setTextZoom}
        positionTextZoom={positionTextZoom}
        setPositionTextZoom={setPositionTextZoom}
        onApplyLinearPattern={handleApplyLinearPattern}
        onOpenPrice={() => setIsPriceOpen(true)}
        onNewDrawing={handleNewDrawing}
        onSaveDrawing={handleSaveDrawing}
        onSaveAsPdf={handleOpenPdfSaveDialog}
        onSaveAsDwg={handleSaveAsDwg}
        onSaveSpecification={onSaveSpecification}
        onSaveAsIGES={handleSaveAsIGES}
        onSaveAsSTEP={handleSaveAsSTEP}
        onSaveAsDXF={handleSaveAsDXF}
        onSaveCuttingModels={handleSaveCuttingModels}
        onLoadDrawing={handleLoadDrawing}
        onGoHome={handleGoHome}
        onBackToAutoConstructor={autoConstructorParams ? handleBackToAutoConstructor : null}
        onSaveForWorkshop={handleSaveForWorkshop}
        workshopInfo={workshopInfo}
        onSendToWorkshop={handleSendToWorkshop}
        isAdmin={isAdmin}
        userId={currentUser?.uid}
        isLoggedIn={!!currentUser}
        onRequestQuote={handleOpenRequestQuote}
      />
      <ModelPreviewDialog
        open={modelPreviewOpen && !!currentUser}
        onClose={() => setModelPreviewOpen(false)}
        getDrawing={getPreviewDrawing}
      />
      <ContainerBox>
        <Sidebar
          onClearCanvas={handleClearCanvas}
          onToggleDrawing={handleToggleDrawing}
          isDrawingEnabled={isDrawingEnabled}
          onToggleAddAnnotationMode={handleToggleAddAnnotationMode}
          isAddAnnotationModeEnabled={isAddAnnotationModeEnabled}
          onToggleDimensionLine={handleToggleDimensionLine}
          isDimensionLineEnabled={isDimensionLineEnabled}
          onToggleBalloonMode={handleToggleBalloonMode}
          isBalloonModeEnabled={isBalloonModeEnabled}
          onToggleOrientation={handleToggleOrientation}
          isLandscape={isLandscape}
          zoom={zoom}
          setZoom={setZoom}
          textZoom={textZoom}
          setTextZoom={setTextZoom}
          positionTextZoom={positionTextZoom}
          setPositionTextZoom={setPositionTextZoom}
          snapThreshold={snapThreshold}
          setSnapThreshold={setSnapThreshold}
          snappingMode={snappingMode}
          setSnappingMode={setSnappingMode}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={canUndo}
          canRedo={canRedo}
        />
        <CanvasContainer>
          <Canvas
            onClearTrigger={clearCanvasTrigger}
            resetClearTrigger={resetClearCanvasTrigger}
            onAddCylinderTrigger={addCylinderTrigger}
            resetAddCylinderTrigger={resetAddCylinderTrigger}
            isDrawingEnabled={isDrawingEnabled}
            isAddAnnotationModeEnabled={isAddAnnotationModeEnabled}
            isDimensionLineEnabled={isDimensionLineEnabled}
            setIsDimensionLineEnabled={setIsDimensionLineEnabled}
            isBalloonModeEnabled={isBalloonModeEnabled}
            isLandscape={isLandscape}
            zoom={zoom}
            setZoom={setZoom}
            textZoom={textZoom}
            setTextZoom={setTextZoom}
            positionTextZoom={positionTextZoom}
            setPositionTextZoom={setPositionTextZoom}
            snapThreshold={snapThreshold}
            snappingMode={snappingMode}
            setUndoRef={(ref) => (undoRef.current = ref)}
            setRedoRef={(ref) => (redoRef.current = ref)}
            setCanUndo={setCanUndo}
            setCanRedo={setCanRedo}
            onAddFittingTrigger={addFittingTrigger}
            addViewRequest={viewRequest}
            resetAddFittingTrigger={resetAddFittingTrigger}
            onApplyLinearPatternTrigger={linearPatternTrigger}
            linearPatternParams={linearPatternParams}
            resetApplyLinearPatternTrigger={resetApplyLinearPatternTrigger}
          setGetObjectsRef={(fn) => { getObjectsRef.current = fn; }}
          onObjectsChanged={() => setObjectsVersion((v) => v + 1)}
          prices={priceList}
          setCanvasMetadataRef={(fn) => { canvasMetadataRef.current = fn; }}
          setSaveDrawingRef={(fn) => { saveDrawingRef.current = fn; }}
          setLoadDrawingRef={(fn) => { loadDrawingRef.current = fn; }}
          loadDrawingData={loadDrawingData}
          setStageRef={(ref) => { stageRef.current = ref; }}
          showWeldMarkers={showWeldMarkers}
          workshopInfo={workshopInfo}
          weldingTotal={weldingTotal}
          userInitials={userInitials}
          />
        </CanvasContainer>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#ffffff', alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: '13.75rem',
              boxSizing: 'border-box',
              padding: '1rem',
              paddingBottom: '0.25rem',
              borderLeft: '0.0625rem solid #e0e0e0',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            <Button
              variant="contained"
              fullWidth
              startIcon={<ViewInArIcon sx={{ fontSize: '1.1rem', color: currentUser ? 'inherit' : '#bdbdbd' }} />}
              onClick={() => {
                if (currentUser) setModelPreviewOpen(true);
                else setGuestFeatureNotice(true);
              }}
              sx={{
                backgroundColor: '#f5f5f5',
                color: currentUser ? '#212121' : '#bdbdbd',
                fontWeight: 600,
                fontSize: '0.8125rem',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                border: '0.0625rem solid #e0e0e0',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#e0e0e0', border: '0.0625rem solid #d0d0d0', boxShadow: 'none' },
              }}
            >
              Просмотр 3D
            </Button>
            <Button
              variant="contained"
              fullWidth
              endIcon={<ArrowDropDownIcon sx={{ color: currentUser ? 'inherit' : '#bdbdbd' }} />}
              onClick={(event) => {
                if (currentUser) setViewsMenuAnchor(event.currentTarget);
                else setGuestFeatureNotice(true);
              }}
              sx={{
                backgroundColor: '#f5f5f5',
                color: currentUser ? '#212121' : '#bdbdbd',
                fontWeight: 600,
                fontSize: '0.8125rem',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                border: '0.0625rem solid #e0e0e0',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#e0e0e0', border: '0.0625rem solid #d0d0d0', boxShadow: 'none' },
              }}
            >
              Добавить вид
            </Button>
            <Menu
              anchorEl={viewsMenuAnchor}
              open={Boolean(viewsMenuAnchor)}
              onClose={() => setViewsMenuAnchor(null)}
            >
              {[
                ['top', 'Вид сверху'],
                ['bottom', 'Вид снизу'],
                ['left', 'Вид слева'],
                ['right', 'Вид справа'],
              ].map(([kind, label]) => (
                <MenuItem
                  key={kind}
                  onClick={() => {
                    setViewsMenuAnchor(null);
                    if (!currentUser) {
                      setGuestFeatureNotice(true);
                      return;
                    }
                    setViewRequest((prev) => ({ count: prev.count + 1, kind }));
                  }}
                >
                  {label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <FastenersPanel />
          <FittingsPanel />
        </Box>
      </ContainerBox>
    <SaveDestinationDialog
      open={saveDestDialog}
      onClose={() => { setSaveDestDialog(false); setPendingSaveData(null); }}
      onSelectDevice={handleSaveToDevice}
      onSelectCloud={handleSaveToCloud}
      isLoggedIn={!!currentUser}
    />
    <RequestQuoteDialog
      open={requestQuoteDialog}
      onClose={() => { setRequestQuoteDialog(false); setPendingSaveData(null); }}
      onGetFiles={handleGenerateFilesForQuote}
      drawingName={pendingSaveData?.safeFileName || 'Чертёж'}
    />
    <PdfWatermarkDialog
      open={pdfWatermarkDialog}
      onClose={() => setPdfWatermarkDialog(false)}
      onSelectWithWatermark={handlePdfWithWatermark}
      onSelectWithoutWatermark={handlePdfWithoutWatermark}
    />
    {isAdmin && <PriceDialog
      open={isPriceOpen}
      onClose={() => setIsPriceOpen(false)}
      getObjects={() => (getObjectsRef.current ? getObjectsRef.current() : { fittings: [], cylinders: [] })}
      prices={priceList}
      onPricesChange={setPriceList}
      // Принудительно пересчитывать useMemo внутри при изменениях на холсте
      version={objectsVersion}
      resetManualMode={resetPriceManualMode}
      // Настройки расчета (для сохранения/загрузки)
      calculationSettings={priceCalculationSettings}
      onCalculationSettingsChange={setPriceCalculationSettings}
      // Визуализация сварок
      showWeldMarkers={showWeldMarkers}
      onShowWeldMarkersChange={setShowWeldMarkers}
      // Метаданные чертежа (totalLength и т.д.)
      drawingMetadata={drawingMetadata}
      // Передача суммы сварки для таблицы цеха
      onWeldingTotalChange={setWeldingTotal}
    />}
    
    {/* Диалог подтверждения */}
    <Dialog
      open={confirmDialog.open}
      onClose={handleCloseConfirmDialog}
    >
      <DialogTitle>{confirmDialog.title}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {confirmDialog.message}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseConfirmDialog} color="inherit">
          {confirmDialog.cancelText}
        </Button>
        <Button onClick={handleConfirmAction} color="primary" variant="contained" autoFocus>
          {confirmDialog.confirmText}
        </Button>
      </DialogActions>
    </Dialog>
    <Snackbar
      open={guestFeatureNotice}
      autoHideDuration={4000}
      onClose={() => setGuestFeatureNotice(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setGuestFeatureNotice(false)}
        severity="info"
        variant="filled"
        sx={{ borderRadius: '0.6rem', fontSize: '0.85rem', alignItems: 'center' }}
      >
        Эта функция доступна только зарегистрированным пользователям
      </Alert>
    </Snackbar>
    </div>
  );
}

export default App;
